// SharesView — gerencia compartilhamentos recebidos e enviados pelo aluno.
//
// Tabs:
//   Recebidos  → convites pra eu aceitar/rejeitar (pending) + meus aceitos
//   Enviados   → convites que eu mandei (com email mascarado pra reduzir leak)
//
// Anti-enumeration:
//   - Email do recipient é exibido mascarado ("ma***@gmail.com")
//   - "Rejected" e "pending" aparecem iguais ("Aguardando" / "Respondido")
//   - Cancelar/revogar muda status mas mantém histórico

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  BookOpen,
  Check,
  CheckCircle2,
  Database,
  Loader2,
  Mic,
  Send,
  Share2,
  Trash2,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { api, ApiError, type ShareRow } from "./api";
import { openConfirm, openAlert } from "./ConfirmPromptModal";

type Tab = "incoming" | "outgoing";

const tab = signal<Tab>("incoming");
const incomingList = signal<ShareRow[]>([]);
const outgoingList = signal<ShareRow[]>([]);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);

async function load(): Promise<void> {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const [inc, out] = await Promise.all([
      api.listShares("incoming"),
      api.listShares("outgoing")
    ]);
    incomingList.value = inc.shares;
    outgoingList.value = out.shares;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      errMsg.value =
        e.status === 403
          ? "Você ainda não tem acesso a Compartilhamentos."
          : "Sessão expirada — entre de novo.";
    } else {
      errMsg.value = e instanceof Error ? e.message : String(e);
    }
    phase.value = "error";
  }
}

export function SharesView() {
  useEffect(() => {
    void load();
  }, []);

  return (
    <div class="space-y-5">
      <header class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <h1 class="flex items-center gap-2 text-xl font-bold">
            <Share2 className="size-5 text-violet-600 dark:text-violet-400" />
            Compartilhamentos
          </h1>
          <p class="mt-1 max-w-2xl text-sm text-muted-foreground">
            Receba bancos, cadernos e gravações de outros alunos — ou compartilhe
            os seus. Cada um mantém seu progresso e suas conversas com a IA
            separados.
          </p>
        </div>
      </header>

      <div class="flex gap-1 border-b border-border">
        <TabButton
          active={tab.value === "incoming"}
          onClick={() => (tab.value = "incoming")}
          label="Recebidos"
          count={
            incomingList.value.filter((s) => s.status === "pending").length
          }
        />
        <TabButton
          active={tab.value === "outgoing"}
          onClick={() => (tab.value = "outgoing")}
          label="Enviados"
          count={outgoingList.value.length}
        />
      </div>

      {phase.value === "loading" && (
        <div class="space-y-2">
          <Skeleton class="h-24 w-full" />
          <Skeleton class="h-24 w-full" />
        </div>
      )}

      {phase.value === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
          <AlertCircle className="mr-2 inline size-4 text-destructive" />
          {errMsg.value}
        </div>
      )}

      {phase.value === "ready" && tab.value === "incoming" && (
        <IncomingList shares={incomingList.value} onReload={load} />
      )}
      {phase.value === "ready" && tab.value === "outgoing" && (
        <OutgoingList shares={outgoingList.value} onReload={load} />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  label,
  count
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? "border-b-2 border-violet-600 text-foreground"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
      {count > 0 && (
        <span
          class={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
            active
              ? "bg-violet-600 text-white"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function IncomingList({
  shares,
  onReload
}: {
  shares: ShareRow[];
  onReload: () => Promise<void>;
}) {
  if (shares.length === 0) {
    return (
      <div class="rounded-md border border-dashed border-border bg-card/40 px-6 py-10 text-center">
        <Share2 className="mx-auto size-10 text-muted-foreground" />
        <h3 class="mt-3 text-base font-semibold">
          Nenhum compartilhamento recebido
        </h3>
        <p class="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
          Quando alguém compartilhar um banco, caderno ou gravação com você,
          aparece aqui pra aceitar.
        </p>
      </div>
    );
  }

  const pending = shares.filter((s) => s.status === "pending");
  const accepted = shares.filter((s) => s.status === "accepted");
  const responded = shares.filter(
    (s) => s.status === "rejected" || s.status === "canceled_by_recipient"
  );

  return (
    <div class="space-y-6">
      {pending.length > 0 && (
        <Section title="Pendentes">
          <ul class="space-y-2">
            {pending.map((s) => (
              <li
                key={s.id}
                class="rounded-md border border-violet-500/40 bg-violet-500/5 p-4"
              >
                <ShareItemBody share={s} />
                <div class="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await api.acceptShare(s.id);
                        await onReload();
                      } catch (e) {
                        void openAlert({
                          title: "Erro ao aceitar",
                          message:
                            e instanceof Error ? e.message : String(e)
                        });
                      }
                    }}
                    class="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-violet-700"
                  >
                    <Check className="size-3.5" />
                    Aceitar
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = await openConfirm({
                        title: "Rejeitar convite?",
                        message:
                          "O remetente verá só que você respondeu, sem saber se aceitou ou rejeitou.",
                        confirmLabel: "Rejeitar"
                      });
                      if (!ok) return;
                      try {
                        await api.rejectShare(s.id);
                        await onReload();
                      } catch (e) {
                        void openAlert({
                          title: "Erro ao rejeitar",
                          message:
                            e instanceof Error ? e.message : String(e)
                        });
                      }
                    }}
                    class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-accent"
                  >
                    <X className="size-3.5" />
                    Rejeitar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {accepted.length > 0 && (
        <Section title="Aceitos">
          <ul class="space-y-2">
            {accepted.map((s) => (
              <li
                key={s.id}
                class="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4"
              >
                <ShareItemBody share={s} />
                <div class="mt-3 flex gap-2">
                  {s.acceptedResourceId && (
                    <a
                      href={linkFor(s.kind, s.acceptedResourceId)}
                      class="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
                    >
                      Abrir
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = await openConfirm({
                        title: "Sair desse compartilhamento?",
                        message:
                          "Você não verá mais esse item entre os compartilhados. O conteúdo já recebido continua seu.",
                        confirmLabel: "Sair"
                      });
                      if (!ok) return;
                      await api.cancelShare(s.id);
                      await onReload();
                    }}
                    class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent"
                  >
                    Sair
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {responded.length > 0 && (
        <Section title="Histórico">
          <ul class="space-y-2">
            {responded.map((s) => (
              <li
                key={s.id}
                class="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground"
              >
                <ShareItemBody share={s} compact />
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function OutgoingList({
  shares,
  onReload
}: {
  shares: ShareRow[];
  onReload: () => Promise<void>;
}) {
  if (shares.length === 0) {
    return (
      <div class="rounded-md border border-dashed border-border bg-card/40 px-6 py-10 text-center">
        <Send className="mx-auto size-10 text-muted-foreground" />
        <h3 class="mt-3 text-base font-semibold">Nenhum convite enviado</h3>
        <p class="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
          Compartilhe pela tela de uma gravação, caderno ou banco — o botão
          fica próximo do título.
        </p>
      </div>
    );
  }

  return (
    <ul class="space-y-2">
      {shares.map((s) => (
        <li
          key={s.id}
          class="rounded-md border border-border bg-card p-4"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <KindIcon kind={s.kind} />
                <span class="truncate font-semibold">
                  {s.resourceTitle ?? "(item)"}
                </span>
                <StatusBadge status={s.status} side="outgoing" />
              </div>
              <p class="mt-1 text-[12px] text-muted-foreground">
                Pra <code class="rounded bg-muted px-1 py-0.5 text-[11px]">
                  {s.recipientEmailNormalized}
                </code>{" "}
                · enviado em {fmtDate(s.createdAt)}
              </p>
              {s.message && (
                <p class="mt-1 text-[12px] italic text-muted-foreground">
                  "{s.message}"
                </p>
              )}
            </div>
            {(s.status === "pending" || s.status === "accepted") && (
              <button
                type="button"
                onClick={async () => {
                  const ok = await openConfirm({
                    title: "Revogar compartilhamento?",
                    message:
                      s.status === "pending"
                        ? "O destinatário não verá mais o convite."
                        : "Aviso: o destinatário mantém o que já recebeu (é dele agora). Só impedimos novos sincronismos.",
                    confirmLabel: "Revogar",
                    danger: true
                  });
                  if (!ok) return;
                  await api.revokeShare(s.id);
                  await onReload();
                }}
                class="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="size-3" />
                Revogar
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function Section({
  title,
  children
}: {
  title: string;
  children: any;
}) {
  return (
    <div>
      <h2 class="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h2>
      {children}
    </div>
  );
}

function ShareItemBody({
  share,
  compact
}: {
  share: ShareRow;
  compact?: boolean;
}) {
  return (
    <div class="min-w-0">
      <div class="flex flex-wrap items-center gap-2">
        <KindIcon kind={share.kind} />
        <span class="truncate font-semibold">
          {share.resourceTitle ?? "(item)"}
        </span>
        {!compact && <StatusBadge status={share.status} side="incoming" />}
      </div>
      <p class={`mt-1 ${compact ? "text-[11px]" : "text-[12px]"} text-muted-foreground`}>
        De <strong>{share.ownerName ?? "Alguém"}</strong>
        {share.ownerEmail && (
          <>
            {" "}
            ·{" "}
            <code class="rounded bg-muted px-1 py-0.5 text-[11px]">
              {share.ownerEmail}
            </code>
          </>
        )}
        {" · "}
        {fmtDate(share.createdAt)}
      </p>
      {!compact && share.message && (
        <p class="mt-1 text-[12px] italic text-muted-foreground">
          "{share.message}"
        </p>
      )}
    </div>
  );
}

function KindIcon({ kind }: { kind: ShareRow["kind"] }) {
  const cls = "size-3.5 text-violet-600 dark:text-violet-400";
  if (kind === "bank") return <Database className={cls} />;
  if (kind === "caderno") return <BookOpen className={cls} />;
  return <Mic className={cls} />;
}

function StatusBadge({
  status,
  side
}: {
  status: ShareRow["status"];
  side: "incoming" | "outgoing";
}) {
  const meta: Record<string, { label: string; cls: string }> = {
    pending: {
      label: side === "outgoing" ? "Aguardando" : "Pendente",
      cls: "bg-violet-500/15 text-violet-700 dark:text-violet-300"
    },
    // No outgoing, "rejected" e "accepted" aparecem genericamente como
    // "Respondido" pra reduzir leak de enumeração.
    accepted: {
      label: side === "outgoing" ? "Respondido" : "Aceito",
      cls:
        side === "outgoing"
          ? "bg-muted text-muted-foreground"
          : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
    },
    rejected: {
      label: side === "outgoing" ? "Respondido" : "Rejeitado",
      cls: "bg-muted text-muted-foreground"
    },
    revoked: { label: "Revogado", cls: "bg-muted text-muted-foreground" },
    canceled_by_recipient: {
      label: "Encerrado",
      cls: "bg-muted text-muted-foreground"
    }
  };
  const m = meta[status] ?? meta.pending;
  return (
    <span
      class={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${m.cls}`}
    >
      {status === "pending" && <Loader2 className="mr-0.5 inline size-2.5 animate-spin" />}
      {status === "accepted" && side !== "outgoing" && (
        <CheckCircle2 className="mr-0.5 inline size-2.5" />
      )}
      {m.label}
    </span>
  );
}

function linkFor(kind: ShareRow["kind"], id: string): string {
  if (kind === "lecture") {
    return `/student/?view=gravador&lectureId=${encodeURIComponent(id)}`;
  }
  if (kind === "caderno") {
    return `/student/?view=ai-study&id=${encodeURIComponent(id)}`;
  }
  return `/student/?view=bancos`;
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return iso;
  }
}
